/**
 * Web sport presentation registry — icons, colours, assessment labels, and lookups.
 * Domain identifiers and schemas live in @shared.
 */

import type { AcademySport, Lesson, RacquetSportId } from '@court-sync/shared';
import {
  DEFAULT_RACQUET_SPORT_ID,
  getAssessmentSkillKeysForSport,
  racquetSportLabel,
} from '@court-sync/shared';

export interface SportPresentation {
  sportId: RacquetSportId;
  label: string;
  slotClasses: string;
  chipClasses: string;
  legendDotClasses: string;
}

const SPORT_SLOT_CLASSES: Record<RacquetSportId, string> = {
  tennis:
    'border border-emerald-200 dark:border-emerald-800 border-l-4 border-l-emerald-500 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-900 dark:text-emerald-100',
  pickleball:
    'border border-cyan-200 dark:border-cyan-800 border-l-4 border-l-cyan-500 bg-cyan-50 dark:bg-cyan-900/30 text-cyan-900 dark:text-cyan-100',
  padel:
    'border border-violet-200 dark:border-violet-800 border-l-4 border-l-violet-500 bg-violet-50 dark:bg-violet-900/30 text-violet-900 dark:text-violet-100',
  badminton:
    'border border-amber-200 dark:border-amber-800 border-l-4 border-l-amber-500 bg-amber-50 dark:bg-amber-900/30 text-amber-900 dark:text-amber-100',
  squash:
    'border border-rose-200 dark:border-rose-800 border-l-4 border-l-rose-500 bg-rose-50 dark:bg-rose-900/30 text-rose-900 dark:text-rose-100',
  other:
    'border border-slate-200 dark:border-slate-700 border-l-4 border-l-slate-500 bg-slate-50 dark:bg-slate-900/30 text-slate-900 dark:text-slate-100',
};

const SPORT_CHIP_CLASSES: Record<RacquetSportId, string> = {
  tennis: 'bg-emerald-600 dark:bg-emerald-500',
  pickleball: 'bg-cyan-600 dark:bg-cyan-500',
  padel: 'bg-violet-600 dark:bg-violet-500',
  badminton: 'bg-amber-500 dark:bg-amber-400',
  squash: 'bg-rose-600 dark:bg-rose-500',
  other: 'bg-slate-600 dark:bg-slate-500',
};

const SPORT_LEGEND_DOT_CLASSES: Record<RacquetSportId, string> = {
  tennis: 'bg-emerald-500',
  pickleball: 'bg-cyan-500',
  padel: 'bg-violet-500',
  badminton: 'bg-amber-400',
  squash: 'bg-rose-500',
  other: 'bg-slate-500',
};

export const SPORT_BADGE_CLASSES: Record<RacquetSportId, string> = {
  tennis: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  pickleball: 'bg-cyan-100 text-cyan-900 dark:bg-cyan-900/40 dark:text-cyan-200',
  padel: 'bg-violet-100 text-violet-900 dark:bg-violet-900/40 dark:text-violet-200',
  badminton: 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200',
  squash: 'bg-rose-100 text-rose-900 dark:bg-rose-900/40 dark:text-rose-200',
  other: 'bg-slate-100 text-slate-800 dark:bg-slate-800/60 dark:text-slate-200',
};

/** Accent tokens for sport filter pills and badges. */
export const SPORT_ACCENT: Record<
  RacquetSportId,
  { dot: string; calendar: string }
> = {
  tennis: { dot: 'bg-emerald-500', calendar: 'border-emerald-500 bg-emerald-50 text-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-100' },
  pickleball: { dot: 'bg-cyan-500', calendar: 'border-cyan-500 bg-cyan-50 text-cyan-900 dark:bg-cyan-950/40 dark:text-cyan-100' },
  padel: { dot: 'bg-violet-500', calendar: 'border-violet-500 bg-violet-50 text-violet-900 dark:bg-violet-950/40 dark:text-violet-100' },
  badminton: { dot: 'bg-amber-400', calendar: 'border-amber-500 bg-amber-50 text-amber-900 dark:bg-amber-950/40 dark:text-amber-100' },
  squash: { dot: 'bg-rose-500', calendar: 'border-rose-500 bg-rose-50 text-rose-900 dark:bg-rose-950/40 dark:text-rose-100' },
  other: { dot: 'bg-slate-500', calendar: 'border-slate-500 bg-slate-50 text-slate-900 dark:bg-slate-950/40 dark:text-slate-100' },
};

export const ASSESSMENT_SKILL_LABELS: Record<string, string> = {
  forehand: 'Forehand',
  backhand: 'Backhand',
  serve: 'Serve',
  volley: 'Volley',
  footwork: 'Footwork',
  strategy: 'Strategy',
  mental_game: 'Mental Game',
  return: 'Return',
  dink: 'Dink',
  drive: 'Drive',
  lob: 'Lob',
  bandeja: 'Bandeja',
  tactics: 'Tactics',
  clear: 'Clear',
  drop: 'Drop',
  smash: 'Smash',
  net_play: 'Net play',
  boast: 'Boast',
  movement: 'Movement',
  technique: 'Technique',
  consistency: 'Consistency',
  match_play: 'Match play',
};

export type AcademySportLookupEntry = {
  sportId: RacquetSportId;
  label: string;
  customLabel?: string | null;
  isActive: boolean;
};

export function getSportPresentation(
  sportId: RacquetSportId,
  customLabel?: string | null
): SportPresentation {
  return {
    sportId,
    label: racquetSportLabel(sportId, customLabel),
    slotClasses: SPORT_SLOT_CLASSES[sportId],
    chipClasses: SPORT_CHIP_CLASSES[sportId],
    legendDotClasses: SPORT_LEGEND_DOT_CLASSES[sportId],
  };
}

export function getAcademySportLabel(
  sport: Pick<AcademySport, 'sportId' | 'customLabel'>
): string {
  return racquetSportLabel(sport.sportId, sport.customLabel);
}

/** Alias used by sport filter/badge components. */
export const getSportLabel = getAcademySportLabel;
export const getAcademySportDisplayLabel = getAcademySportLabel;

export function buildAcademySportLookup(
  sports: readonly AcademySport[]
): Map<string, AcademySportLookupEntry> {
  const map = new Map<string, AcademySportLookupEntry>();
  for (const sport of sports) {
    map.set(sport.id, {
      sportId: sport.sportId,
      label: getAcademySportLabel(sport),
      customLabel: sport.customLabel,
      isActive: sport.isActive,
    });
  }
  return map;
}

export function resolvePrimaryAcademySportId(sports: readonly AcademySport[]): string | undefined {
  const primary = sports.find((s) => s.isPrimary && s.isActive) ?? sports.find((s) => s.isActive);
  return primary?.id;
}

export function resolvePrimaryAcademySport(
  sports: readonly AcademySport[]
): AcademySport | null {
  return sports.find((s) => s.isPrimary && s.isActive) ?? sports.find((s) => s.isActive) ?? null;
}

export function getActiveAcademySports(
  sports: readonly AcademySport[] | null | undefined
): AcademySport[] {
  return (sports ?? []).filter((s) => s.isActive);
}

export const activeAcademySports = getActiveAcademySports;

export function isMultiSportAcademy(sports: readonly AcademySport[]): boolean {
  return getActiveAcademySports(sports).length > 1;
}

export function resolveAcademySport(
  academySports: readonly AcademySport[],
  academySportId?: string | null
): AcademySport | null {
  if (academySportId) {
    const match = academySports.find((s) => s.id === academySportId && s.isActive);
    if (match) return match;
  }
  return resolvePrimaryAcademySport(academySports);
}

export function resolveLessonSport(
  lesson: Pick<Lesson, 'academySportId'>,
  academySports?: readonly AcademySport[] | null
): SportPresentation {
  const sports = getActiveAcademySports(academySports ?? []);
  const match = lesson.academySportId
    ? sports.find((s) => s.id === lesson.academySportId)
    : resolvePrimaryAcademySport(sports);
  const sportId = match?.sportId ?? DEFAULT_RACQUET_SPORT_ID;
  return getSportPresentation(sportId, match?.customLabel);
}

export function formatAssessmentSkillLabel(key: string): string {
  if (ASSESSMENT_SKILL_LABELS[key]) return ASSESSMENT_SKILL_LABELS[key];
  return key
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export function getSportBadgeClasses(sportId: RacquetSportId): string {
  return SPORT_BADGE_CLASSES[sportId] ?? SPORT_BADGE_CLASSES.other;
}

/** Shared solid tile for selection cards and prominent iconography (not sport-tinted). */
export function getSportIconTileClasses(_sportId: RacquetSportId): string {
  return 'bg-slate-700 dark:bg-slate-600';
}

export function getAssessmentSkillKeysForAcademySport(
  sport: Pick<AcademySport, 'sportId'> | null | undefined
): readonly string[] {
  return getAssessmentSkillKeysForSport(sport?.sportId ?? DEFAULT_RACQUET_SPORT_ID);
}

export function getAssessmentSkillsForSport(sportId: RacquetSportId): readonly string[] {
  return getAssessmentSkillKeysForSport(sportId);
}
